import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  ListGroup,
  ListGroupItem,
  Modal,
  OverlayTrigger,
  Popover,
  Row,
  Table,
} from 'react-bootstrap';
import { Route, RouteComponentProps, withRouter } from 'react-router-dom';
import { Condition, sqlFormOf } from '../../../models/sqlform';
import TreeHistory from '../../../models/tree/TreeHistory';
import TreeStructure from '../../../models/tree/TreeStructure';
import Tree from '../../../models/tree/TreeLite';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import { extractNodeFromTreeStructure, flattenTree } from '../../../utils';
import checkPermissions from '../../components/CheckPermissions';
import NodeInsightsPanel from '../../components/insights/NodeInsightsPanel';
import Error from '../../components/error/Error';
import TreePerformanceCharts from '../../components/trees/TreePerformanceCharts';
import { leavesOf } from '../../components/trees/treePerformance';
import {
  findNode,
  nodeDepth,
  splitsAlready,
} from '../../components/trees/findNode';
import TreeNodeTable from '../../components/trees/TreeNodeTable';
import AUCCurveLegacy from '../../components/trees/AUCCurveLegacy';
import { SegmentLegend } from '../../components/trees/NodeTraits';
import NodeInspector from '../../components/trees/NodeInspector';
import TreeSideBar from '../../components/trees/TreeSideBar';
import Spinner from '../../components/Spinner';
import { fetchTreeInsightsEnabled } from '../../components/model/rgipChatApi';
import { onTreeRefresh } from '../../components/model/treeRefreshBus';
import CustomToggle from '../../components/generic_components/CustomToggle';
import TreeGradient from '../../assets/tree_gradient.svg';
import '../../components/trees/segmented.css';
// The manual insights shelf is drawn here rather than inside the panel, because it
// is what stands in for the panel while the panel is not mounted.
import '../../components/insights/nodeInsights.css';

// Hoisted out of render deliberately. React.lazy called during render builds a
// new component type on every pass, so React unmounted and remounted the whole
// tree each time TreePage rendered. That was invisible while the tree had no
// mount behaviour; once it fitted the view on mount, selecting a node threw the
// camera back to the fitted position on every click.
const TreeViz = React.lazy(() => import('../../components/trees/TreeViz'));

interface TreePageProps extends RouteComponentProps<any> {
  store?: Store;
}

type TreeView = 'vertical' | 'horizontal' | 'table';

/**
 * Icons, not words, and the same icon rotated for the two canvas layouts.
 *
 * As text pills this control was indistinguishable from the Tree 1 / Tree 2 /
 * Tree 3 picker directly above it: same component, same size, same shape, two
 * completely different things to change. A rotated sitemap says which way the
 * tree runs better than either word does, and the pair reads as one choice about
 * orientation rather than three unrelated options.
 *
 * The label survives as the tooltip and the accessible name, so nothing is lost
 * for a screen reader or for anyone who does not recognise the glyph.
 */
const TREE_VIEWS: { key: TreeView; label: string; icon: string }[] = [
  { key: 'vertical', label: 'Vertical', icon: 'fas fa-sitemap' },
  {
    key: 'horizontal',
    label: 'Horizontal',
    icon: 'fas fa-sitemap fa-rotate-270',
  },
  { key: 'table', label: 'Table', icon: 'fas fa-table' },
];

interface TreePageState {
  actualTransitionCoords: {
    x: number;
    y: number;
    zoom: number;
  };
  /**
   * Nodes hovered anywhere on the page. Drives the preview node panel, so a
   * tree can be read by moving across it without selecting anything.
   */
  hoveredNodes: number[] | null;
  view: TreeView;
  /**
   * Matches the old page's default. It is now purely a display choice: the
   * baseline comes from the node, so showing or hiding a bucket moves no number.
   */
  hideUnknown: boolean;
  /**
   * Whether this tenant gets the tree page's insights at all: the performance charts,
   * the manual attribute panel and the AI review. Null while the flag is being read.
   *
   * Null renders NONE of them, deliberately. Showing them and then taking them away a
   * moment later is worse than a beat of the old page, and it is the same fail-closed
   * reading the server side takes.
   */
  treeInsights: boolean | null;
  /**
   * Whether the manual insights panel ("Explore manually") is open. Closed until
   * the reader opens it, on every visit to the page.
   */
  manualInsights: boolean;
  /** Whether the recall and precision charts above the tree are expanded. */
  performanceOpen: boolean;
}

const TreePage = inject('store')(
  observer(
    class TreePage extends React.Component<TreePageProps, TreePageState> {
      constructor(props: TreePageProps) {
        super(props);

        makeObservable(this, {
          displayCommitFile: action,
        });

        this.state = {
          actualTransitionCoords: { x: 350, y: 20, zoom: 0.5 },
          hoveredNodes: null,
          view: 'vertical',
          hideUnknown: true,
          treeInsights: null,
          manualInsights: false,
          // Starts closed, so the page opens on the tree, which is what the
          // reader came to edit. Closed the charts still show their totals, so
          // nothing is invisible, only folded.
          performanceOpen: false,
        };
        this.applySplit = this.applySplit.bind(this);
        this.deleteSplit = this.deleteSplit.bind(this);
        this.selectNode = this.selectNode.bind(this);
        this.deselectNode = this.deselectNode.bind(this);
        this.setHoveredNodes = this.setHoveredNodes.bind(this);
        this.resetTree = this.resetTree.bind(this);
        this.closeResetPopup = this.closeResetPopup.bind(this);
        this.toggleResetTree = this.toggleResetTree.bind(this);
        this.closeAdvancedMode = this.closeAdvancedMode.bind(this);
        this.toggleAdvancedMode = this.toggleAdvancedMode.bind(this);
        this.closeHistory = this.closeHistory.bind(this);
        this.toggleHistory = this.toggleHistory.bind(this);
        this.displayCommitFile = this.displayCommitFile.bind(this);
        this.handleTreeDefinitionChange =
          this.handleTreeDefinitionChange.bind(this);
        this.updateTreeDefinition = this.updateTreeDefinition.bind(this);
        this.setActualTransitionCoords =
          this.setActualTransitionCoords.bind(this);
      }

      async componentDidMount() {
        const { treeId } = this.props.match.params;
        const { tenant, modelId } = this.props.store;
        // Fetched here rather than by the manual insights panel, which used to be
        // the only thing that needed them and is now collapsed on arrival. The
        // diagnostics panel decides whether a candidate can be applied from these,
        // so without them no Apply button would ever appear.
        this.props.store.fetchFormFields();
        // The master gate for everything this page grew. Not awaited with the tree: the
        // tree must not wait on a flag, and the sections it controls are not drawn until
        // it answers anyway.
        fetchTreeInsightsEnabled(String(tenant))
          .then((treeInsights) => this.setState({ treeInsights }))
          .catch(() => this.setState({ treeInsights: false }));
        await Promise.all([
          this.props.store.loadTree(Number(treeId)),
          // Thresholds are model level and colour the nodes by fit segment. A
          // cheap config GET, not the performance computation, and failing it
          // only means the tree keeps its gradient colours.
          this.props.store.ensemblingPage
            .loadConfiguration(tenant, modelId)
            .catch(() => undefined),
        ]);

        // Something other than this browser can now change the tree: the split
        // routes an agent calls are the same three the UI calls, and a write that
        // did not come from a click leaves the pane showing a tree that no longer
        // exists. `refreshTree` reloads without emptying `definition` first, so
        // the canvas keeps its viewport and any open panel stays open.
        this.offTreeRefresh = onTreeRefresh((target) => {
          const open = Number(this.props.match.params.treeId);
          // A refresh naming another tree is not this pane's business. An
          // unnamed one is, because the emitter did not know and this pane does.
          if (target !== null && target !== open) return;
          this.props.store.refreshTree(open).catch(() => undefined);
        });
      }

      /** Unsubscribe handle for the agent driven refresh. */
      offTreeRefresh?: () => void;

      async componentDidUpdate(prevProps: TreePageProps) {
        const { treeId } = this.props.match.params;
        if (treeId !== prevProps.match.params.treeId) {
          await this.props.store.loadTree(Number(treeId));
        }
      }

      componentWillUnmount() {
        this.offTreeRefresh?.();
      }

      /**
       * Split a node on a condition an insights panel has already had confirmed.
       *
       * Writes the tree directly. `insertChildNode` posts
       * `treesPage.currentSplitCondition` to the node's `/add` endpoint and reloads
       * the tree; it reads nothing the node panel owns, so the node does not have
       * to be selected and no form has to be open. That is what removed the
       * two-step dance this used to do: the condition was written into the form,
       * the form lived in the node panel, and `NodeInspector.componentDidMount`
       * wiped it with `resetSplitCondition` when the panel mounted, so the node had
       * to be selected first and the condition written on a later commit. Nothing
       * mounts between these two lines.
       *
       * The selection is deliberately left alone. Selecting the split node would
       * scroll the tree and swap the insights table out from under the panel the
       * user is reading, and they can already see the result: the tree redraws.
       */
      async applySplit(nodeId: number, conditions: Condition[]) {
        const { store } = this.props;
        const treeId = Number(this.props.match.params.treeId);
        const tree = store.treesPage.trees[treeId - 1];
        // Through `sqlFormOf` rather than assembled here: a form saved with an
        // empty `conditionLogic` renders as empty SQL and writes a split that
        // tests nothing, and the old path only escaped that because the modal's
        // `ConditionsForm` filled the logic in on mount.
        // Whatever the candidate needs, ANDed. One condition for a cut on a single
        // column; four for the clean-companies root, which tests every lead-quality
        // flag at once. `sqlFormOf` builds the `$1 AND $2 ...` logic either way.
        store.updateSplitCondition(sqlFormOf(conditions));
        // Insert is only correct on a leaf. `editNode` with insert copies the node
        // into both new branches BEFORE reassigning its children, so on a node that
        // already splits each branch inherits the old condition and the whole
        // existing subtree, and the tree serialises twice. A recommendation to
        // replace a cut that separates nothing always lands on such a node, so the
        // insert path was the wrong one for exactly the findings that need it most.
        //
        // Decided from the structure rather than from the finding's `action`: the
        // review can be stale, and the cost of believing it is a duplicated subtree.
        const node = findNode(tree?.definition?.structure, nodeId);
        if (splitsAlready(node)) {
          await store.replaceSplitCondition(treeId, tree?.name, nodeId);
        } else {
          await store.insertChildNode(treeId, tree?.name, nodeId);
        }
      }

      /**
       * Remove a node's split, folding its two branches back into it.
       *
       * The remedy for a group too small to trust, which cannot be repaired where it
       * is found: the only move is on the split that produced it. That is why the
       * panel's delete button targets the parent while its node buttons still point
       * at the small group, so the reader can look at what they are about to lose.
       */
      async deleteSplit(nodeId: number) {
        const { store } = this.props;
        const treeId = Number(this.props.match.params.treeId);
        await store.deleteSplitCondition(
          treeId,
          store.treesPage.trees[treeId - 1]?.name,
          nodeId
        );
      }

      selectNode(
        nodeId: number,
        actualTransitionCoords?: { x: number; y: number; zoom: number }
      ) {
        const { tenant, modelId } = this.props.store;
        const { treeId } = this.props.match.params;
        if (actualTransitionCoords) {
          this.setActualTransitionCoords(actualTransitionCoords);
        }
        this.props.history.push({
          pathname: `/tenant/${tenant}/models/${modelId}/model/trees/${treeId}/nodes/${nodeId}`,
        });
      }

      // Closing the node panel drops the node out of the URL, which is the same
      // state the page opens in. The route is what decides whether a node is
      // selected, so there is nothing else to unset.
      deselectNode() {
        const { tenant, modelId } = this.props.store;
        const { treeId } = this.props.match.params;
        this.props.history.push({
          pathname: `/tenant/${tenant}/models/${modelId}/model/trees/${treeId}`,
        });
      }

      // Guarded against setting the same value: hovering moves across a row or a
      // node fire repeatedly, and each setState re-renders the tree.
      setHoveredNodes(hoveredNodes: number[] | null) {
        if (this.state.hoveredNodes?.join() !== hoveredNodes?.join()) {
          this.setState({ hoveredNodes });
        }
      }

      setActualTransitionCoords(coords: {
        x: number;
        y: number;
        zoom: number;
      }) {
        const { actualTransitionCoords } = this.state;

        if (
          actualTransitionCoords.x !== coords.x ||
          actualTransitionCoords.y !== coords.y ||
          actualTransitionCoords.zoom !== coords.zoom
        ) {
          this.setState({
            actualTransitionCoords: coords,
          });
        }
      }

      toggleResetTree() {
        this.props.store.toggleResetTreePopup();
      }

      closeResetPopup() {
        this.props.store.toggleResetTreePopup();
      }

      toggleAdvancedMode() {
        this.props.store.toggleAdvancedModePopup();
      }

      closeAdvancedMode() {
        this.props.store.toggleAdvancedModePopup();
      }

      toggleHistory(treeId: number) {
        return async () => {
          await this.props.store.getTreeHistory(treeId);
          this.props.store.toggleHistoryPopup();
        };
      }

      closeHistory() {
        this.props.store.toggleHistoryPopup();

        this.props.store.treesPage.treeHistory =
          this.props.store.treesPage.treeHistory.map((item) => ({
            ...item,
            active: false,
          }));
        this.props.store.treesPage.commitContent = '';
        this.props.store.treesPage.treeHistory = [];
      }

      displayCommitFile(treeId: number, commit: TreeHistory) {
        return async () => {
          this.props.store.treesPage.treeHistory =
            this.props.store.treesPage.treeHistory.map((item) => ({
              ...item,
              active:
                item.active && item.sha !== commit.sha ? false : item.active,
            }));
          // eslint-disable-next-line no-param-reassign
          commit.active = true;
          this.props.store.treesPage.isCommitDisplayLoading = true;
          await this.props.store.getCommitContent(treeId, commit.sha);
        };
      }

      async resetTree(treeId: number) {
        await this.props.store.resetTree(treeId);
      }

      handleTreeDefinitionChange(e: any) {
        const treeId = Number(this.props.match.params.treeId);
        this.props.store.treesPage.trees[treeId - 1].definition.yaml =
          e.target.value;
      }

      async updateTreeDefinition(
        treeId: number,
        treeName: string,
        commitContent?: string
      ) {
        const { historyIsShown, advancedModeIsShown } =
          this.props.store.treesPage;

        await this.props.store.updateTreeDefinition(
          treeId,
          treeName,
          commitContent
        );

        if (historyIsShown) this.closeHistory();
        if (advancedModeIsShown) this.closeAdvancedMode();
      }

      /**
       * Open, and whatever the reader last said once they have said anything.
       *
       * It used to open only when the AI review was absent, since the review was
       * the primary path when present. The review is gone, so this panel is the
       * path and starts open.
       */
      /**
       * The node's own population and conversions come from the tree structure,
       * never from summing the buckets. Summing is only right when the buckets
       * partition the node, and a multi-valued attribute puts a lead in several
       * at once, which is how the old page came to measure lift against a
       * baseline belonging to no node.
       */
      /**
       * The two cards this page carried before the revamp, for tenants the
       * revamp is switched off for.
       *
       * They were deleted when their replacements arrived, but the replacements
       * are behind `studio-tree-insights` while the deletion was not, so a
       * tenant with the flag off lost the tree's recall reading and its split
       * suggestions and got nothing in their place. The flag is supposed to mean
       * "the page as it was", and without these it meant "the page as it was,
       * minus two cards".
       *
       * Drawn from `tree.definition.performances` and the leaves, exactly as
       * before. Deliberately not rebuilt in the new visual language: this is the
       * fallback, and it should look like what it is falling back to.
       */
      /**
       * The pieces the pre-revamp tree page is built from, lifted from
       * `git show cdeb6a8d:src/front/pages/model/TreePage.tsx` rather than
       * rebuilt, so they are what they were.
       *
       * One edit: the node table's row hover drove a tooltip on the recharts
       * AUC chart through `this.chartRef`, and that chart is gone.
       */
      preRevampPieces(tree: Tree, nodeId: number) {
        let smallLeaves;
        let bigLeaves;

        if (tree && tree.definition && tree.definition.structure) {
          const allNodes = flattenTree(tree.definition.structure);
          const leaves = allNodes
            .filter((n) => n.children.length === 0)
            .sort((t, t2) => t.population - t2.population);
          const leavesBreakpoint = Math.ceil(leaves.length / 2);
          smallLeaves = leaves.splice(0, leavesBreakpoint);
          bigLeaves = leaves.reverse();
        }

        let performances = <div />;
        if (tree && tree.definition && tree.definition.performances) {
          const perfs = tree.definition.performances.map((perf, index) => {
            return (
              <p key={`perf_${perf.customersShare}_id${index}`}>
                <b>{Math.round(perf.customersShare)}%</b> of customers make{' '}
                <b>{Math.round(perf.conversionsShare)}%</b> of conversions.
              </p>
            );
          });
          performances = (
            <Card className="mb-2">
              <Card.Body>
                <Card.Title>Tree performance</Card.Title>
                <div className="mt-4">{perfs}</div>
              </Card.Body>
            </Card>
          );
        }

        let conversionRatesByNode = <div />;
        if (tree && tree.definition && tree.definition.precisions) {
          const tableData = tree.definition.precisions.map((node) => {
            return {
              nodes: node.ids.join(', '),
              conversions: Number(node.conversions) || 0,
              population: Number(node.population),
              percentTotalConversion: node.percentTotalConversion.toFixed(2),
              percentTotalPopulation: node.percentTotalPopulation.toFixed(2),
              conversionRate: Number((node.conversionRate * 100).toFixed(2)),
            };
          });
          conversionRatesByNode = (
            <Card>
              <Card.Body>
                <Card.Title>Nodes ranked by conversion rate</Card.Title>
                <Table className="mt-4" striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Conversions</th>
                      <th>Population</th>
                      <th>Population %</th>
                      <th>Conversion %</th>
                      <th>Conversion rate %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData
                      .sort((d1, d2) => d2.conversionRate - d1.conversionRate)
                      .map((data, index) => {
                        return (
                          <tr key={`nodes_${data.nodes}_id${index}`}>
                            <td>{data.nodes}</td>
                            <td>{data.conversions}</td>
                            <td>{data.population}</td>
                            <td>{data.percentTotalPopulation}</td>
                            <td>{data.percentTotalConversion}</td>
                            <td>{data.conversionRate}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          );
        }

        const getLeafButtonsGroup = (leaves: TreeStructure[]) => {
          return (
            <ButtonGroup vertical>
              {leaves.map((leaf: TreeStructure) => (
                <Button
                  key={leaf.id}
                  active={nodeId ? leaf.id === nodeId : false}
                  onClick={() => this.selectNode(leaf.id)}
                  variant="outline-secondary"
                >
                  Node {leaf.id}
                  <br />
                  {Math.round(leaf.percentPopulation * 100)}% pop -{' '}
                  {Math.round(leaf.percentConversions * 100)}% conv
                </Button>
              ))}
            </ButtonGroup>
          );
        };

        return {
          bigLeaves,
          smallLeaves,
          getLeafButtonsGroup,
          performances,
          conversionRatesByNode,
        };
      }

      /**
       * The tree page as it stood before the revamp, structure and all.
       *
       * Restored from `git show cdeb6a8d:src/front/pages/model/TreePage.tsx`
       * rather than reassembled: the pieces were recovered one at a time and
       * dropped into the revamped layout, which put the recommendations under
       * the tree and the node panel across the full width beneath it. The
       * arrangement IS the page, so it comes back with them: recommendations at
       * `sm={2}` on the left, the tree at `sm={7}`, the node sidebar at
       * `sm={3}` on the right, and the node table beside the performance card
       * and the curve below.
       *
       * The only edit is the AUC curve's `ref`, which pointed at a recharts
       * chart this page no longer has.
       */
      renderPreRevampTree(args: {
        tree: any;
        treeId: number;
        nodeId: number;
        tenant: number;
        modelId: number;
        updatingTree: boolean;
        resetingTree: boolean;
        isAllowedToEdit: boolean;
        bigLeaves: TreeStructure[];
        smallLeaves: TreeStructure[];
        getLeafButtonsGroup: (leaves: TreeStructure[]) => JSX.Element;
        conversionRatesByNode: JSX.Element;
        performances: JSX.Element;
      }) {
        const {
          tree,
          treeId,
          nodeId,
          tenant,
          modelId,
          updatingTree,
          resetingTree,
          isAllowedToEdit,
          bigLeaves,
          smallLeaves,
          getLeafButtonsGroup,
          conversionRatesByNode,
          performances,
        } = args;
        return (
          <>
            <Row>
              <Col sm={2}>
                {bigLeaves.length && smallLeaves.length && (
                  <Card border="light">
                    <Card.Body>
                      <Card.Title>💡 Recommendations</Card.Title>
                      <p>Big nodes you may want to split</p>
                      {getLeafButtonsGroup(bigLeaves)}
                      <p className="mt-2">Small nodes you may want to trim</p>
                      {getLeafButtonsGroup(smallLeaves)}
                    </Card.Body>
                  </Card>
                )}
              </Col>
              <Col sm={7}>
                {!updatingTree ? (
                  <React.Suspense fallback={<Spinner />}>
                    <Card>
                      <Card.Body>
                        <Row>
                          <Col md="auto">
                            <Card.Title>Tree Visualization</Card.Title>
                          </Col>
                          <Col className="float-end text-end">
                            <img
                              src={TreeGradient}
                              alt="Tree gradient scale"
                              style={{
                                maxHeight: '300px',
                                maxWidth: '100%',
                                fill: 'white',
                              }}
                            />
                          </Col>
                          {checkPermissions(
                            PROFILES_PERMISSIONS.ARCHITECT,
                            <Col className="float-end" xs={1}>
                              <Dropdown className="float-end">
                                <Dropdown.Toggle
                                  as={CustomToggle}
                                ></Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={this.toggleHistory(treeId)}
                                    disabled={!isAllowedToEdit}
                                  >
                                    History / undo
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={this.toggleAdvancedMode}
                                    disabled={!isAllowedToEdit}
                                  >
                                    Advanced mode
                                  </Dropdown.Item>

                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    onClick={this.toggleResetTree}
                                    disabled={resetingTree || !isAllowedToEdit}
                                  >
                                    {resetingTree
                                      ? 'Reseting...'
                                      : 'Reset to default'}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </Col>
                          )}
                        </Row>

                        <TreeViz
                          treeId={treeId}
                          nodeId={nodeId}
                          revamped={false}
                          store={this.props.store}
                          selectNode={this.selectNode}
                          setActualTransitionCoords={
                            this.setActualTransitionCoords
                          }
                          actualTransitionCoords={
                            this.state.actualTransitionCoords
                          }
                        />
                      </Card.Body>
                    </Card>
                  </React.Suspense>
                ) : (
                  <Spinner />
                )}
              </Col>
              <Col sm={3}>
                <Route
                  exact
                  path={`/tenant/${tenant}/models/${modelId}/model/trees/${treeId}/nodes/:nodeId`}
                  render={(props: any) => (
                    <TreeSideBar
                      treeId={treeId}
                      nodeId={Number(props.match.params.nodeId)}
                      treeName={tree.name}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col sm={8}>{conversionRatesByNode}</Col>
              <Col sm={4}>
                {performances}
                {tree && tree.definition && tree.definition.precisions && (
                  <AUCCurveLegacy precisions={tree.definition.precisions} />
                )}
              </Col>
            </Row>
          </>
        );
      }

      /** The explainer as it stood before the revamp folded it into a popover. */
      renderTreeHelpCard() {
        return (
          <Card border="light" className="mb-4">
            <Card.Body>
              <p>
                Each node of the tree splits the total population into
                populations with different conversion rates to isolate the very
                good leads from the low converting populations to predict the
                Fit of a lead.
              </p>
              <a
                href="https://help.madkudu.com/docs/customer-fit-how-to-read-a-decision-tree"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none me-1"
              >
                <i aria-hidden className="fas fa-book-open" /> How to read a
                tree{' '}
              </a>
              and
              <a
                href="https://help.madkudu.com/docs/customer-fit-how-to-create-or-edit-a-decision-tree"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none ms-1"
              >
                <i aria-hidden className="fas fa-book-open" />
                How to build/edit a tree
              </a>
            </Card.Body>
          </Card>
        );
      }

      renderInsights(treeId: number, structure: TreeStructure, nodeId: number) {
        if (!structure) return null;
        const shown = nodeId || 1;
        const node = extractNodeFromTreeStructure(structure, shown);
        if (!node) return null;
        const open = this.state.manualInsights;

        // The card is drawn either way and grows rather than appears, and the
        // control keeps its wording across both states, so opening it does not
        // rename the section or move anything below it. It sits at the head of
        // the insights row, directly under the diagnostics panel in the left
        // column, which is what makes it read as the other way to do the same
        // job rather than as an unrelated collapsed section.
        return (
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <button
                  type="button"
                  className="nins-shelf"
                  aria-expanded={open}
                  onClick={() => this.setState({ manualInsights: !open })}
                >
                  <span className="nins-shelf-mark" aria-hidden>
                    {open ? '▼' : '►'}
                  </span>
                  <span className="nins-shelf-label">Explore manually</span>
                  <span className="nins-shelf-hint">
                    Node {shown}, attribute by attribute
                  </span>
                </button>
                {open && (
                  <Form.Switch
                    id="nins_include_unknown"
                    label="Include unknown values"
                    checked={!this.state.hideUnknown}
                    onChange={() =>
                      this.setState({ hideUnknown: !this.state.hideUnknown })
                    }
                  />
                )}
              </div>
              {open && (
                <NodeInsightsPanel
                  treeId={treeId}
                  nodeId={shown}
                  conditions={node.rawSumConditions}
                  node={{
                    population: Number(node.population),
                    conversions: Number(node.conversions),
                  }}
                  // Where this node sits, so the panel can refuse a split on a
                  // column that stores "no data" as a real value at the top of
                  // the tree. `?? undefined` on purpose: a node the structure
                  // does not hold must not read as the root.
                  depth={nodeDepth(structure, shown) ?? undefined}
                  hideUnknown={this.state.hideUnknown}
                  // Same route the AI panel uses. The panel confirms the split
                  // where it recommended it and this writes it; no node has to be
                  // selected and the split modal is not involved.
                  onApplySplit={this.applySplit}
                />
              )}
            </Card.Body>
          </Card>
        );
      }

      render() {
        const { tenant, modelId, treesPage, isAllowedToEdit } =
          this.props.store;
        const {
          trees,
          resetingTree,
          resetTreeIsShown,
          advancedModeIsShown,
          historyIsShown,
          updatingTree,
          savingSplitConditionError,
          deletingSplitConditionError,
          treeHistory,
          commitContent,
          isCommitDisplayLoading,
        } = treesPage;
        const treeId = Number(this.props.match.params.treeId);
        const tree = trees[treeId - 1];

        const re = this.props.location.pathname.match(/nodes\/([0-9]+)/);
        const nodeId: number = re && re[1] && Number(re[1]);

        const confirmResetDialog = (
          <Modal onHide={this.closeResetPopup} show={resetTreeIsShown}>
            <Modal.Header>
              <Modal.Title>
                Are you sure you want to reset Tree {treeId}?
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Consider using the <code>History / Undo</code> button to undo
                your changes to the tree.
              </p>
            </Modal.Body>
            <Modal.Footer>
              {!resetingTree && (
                <Button
                  onClick={this.closeResetPopup}
                  disabled={resetingTree}
                  variant="outline-primary"
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => this.resetTree(treeId)}
                disabled={resetingTree}
              >
                {resetingTree ? 'Reseting tree...' : 'Reset tree'}
              </Button>
            </Modal.Footer>
          </Modal>
        );

        let advancedModeDialog;
        if (tree && tree.definition) {
          advancedModeDialog = (
            <Modal
              onHide={this.closeAdvancedMode}
              show={advancedModeIsShown}
              size="lg"
            >
              <Modal.Header>
                <Modal.Title>Tree {treeId} - Advanced mode</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Please import the code from Workbench:</p>
                <FormControl
                  as="textarea"
                  placeholder="Tree definition"
                  rows={15}
                  onChange={this.handleTreeDefinitionChange}
                  autoFocus={true}
                  value={tree.definition.yaml}
                />
              </Modal.Body>
              <Modal.Footer>
                {!updatingTree && (
                  <Button
                    variant="outline-primary"
                    onClick={this.closeAdvancedMode}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={() => this.updateTreeDefinition(treeId, tree.name)}
                  disabled={
                    updatingTree ||
                    !tree.definition.yaml ||
                    tree.definition.yaml.trim().length === 0
                  }
                >
                  {updatingTree ? 'Importing tree...' : 'Import tree'}
                </Button>
              </Modal.Footer>
            </Modal>
          );
        }

        const historyDialog = (
          <Modal onHide={this.closeHistory} show={historyIsShown} size="lg">
            <Modal.Header>
              <Modal.Title>Past commits for tree {treeId}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col xs={4}>
                  <ListGroup
                    style={{ maxHeight: '500px', overflowY: 'scroll' }}
                  >
                    {treeHistory?.map((item, index) => (
                      <ListGroupItem
                        active={item.active}
                        onClick={this.displayCommitFile(treeId, item)}
                        key={index}
                      >
                        {item.author === 'Paul Cothenet'
                          ? 'Springbok'
                          : item.author}
                        <br />
                        {item.date}
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </Col>
                <Col xs={8}>
                  {isCommitDisplayLoading ? (
                    <div style={{ marginTop: '180px' }}>
                      <Spinner />
                    </div>
                  ) : (
                    <FormControl
                      as="textarea"
                      rows={3}
                      style={{ width: '100%', height: '500px' }}
                      value={commitContent}
                      disabled={true}
                    />
                  )}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={this.closeHistory} variant="outline-primary">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  this.updateTreeDefinition(treeId, tree.name, commitContent)
                }
                disabled={isCommitDisplayLoading || updatingTree}
              >
                {updatingTree ? 'Updating...' : 'Update'}
              </Button>
            </Modal.Footer>
          </Modal>
        );

        // Everything the revamp changed about how the tree LOOKS hangs off this,
        // so a tenant it is switched off for gets the tree they had before:
        // numbered circles, the library's own links, one view, the old sidebar.
        //
        // `=== true` rather than truthiness, for the same reason as everywhere
        // else on this page: the flag is `boolean | null` and `null` means still
        // resolving, which must draw neither shape rather than the old one.
        const revamped = this.state.treeInsights === true;

        // The tree itself, held in one place because it renders in two: inside the
        // right-hand column when the review is open, and full width beneath both
        // collapsed bars when it is not. The camera lives in this component's state,
        // so moving between the two does not lose the reader's position.
        const treeCard = !updatingTree ? (
          <React.Suspense fallback={<Spinner />}>
            <Card>
              <Card.Body>
                {/* Centred, with the title's own bottom margin dropped and the
                    spacing moved onto the row.
                    
                    The row had no bottom margin and `.tvz-stage` follows it
                    immediately, so the segmented control, which is the tallest
                    thing in the header at about 33px against the title's 24px,
                    ran straight into the top of the canvas. Leaving the title's
                    `0.5rem` there would not have fixed it: that margin sits
                    inside the title's own column and does nothing for the column
                    next to it. The gap belongs to the row, which is what the
                    canvas actually butts against. */}
                <Row className="align-items-center mb-3">
                  <Col md="auto">
                    <Card.Title className="d-flex align-items-center gap-2 mb-0">
                      Tree Visualization
                      {/* The popover form of the explainer. Off the flag the
                          same words stand in a card above the tree, the way
                          they did, so the help is not offered twice. */}
                      {revamped && (
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          placement="bottom-start"
                          overlay={
                            <Popover id="tree-help">
                              <Popover.Body>
                                <p>
                                  Each node of the tree splits the total
                                  population into populations with different
                                  conversion rates to isolate the very good
                                  leads from the low converting populations to
                                  predict the Fit of a lead.
                                </p>
                                <a
                                  href="https://help.madkudu.com/docs/customer-fit-how-to-read-a-decision-tree"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="d-block text-decoration-none"
                                >
                                  <i
                                    aria-hidden
                                    className="fas fa-book-open me-1"
                                  />
                                  How to read a tree
                                </a>
                                <a
                                  href="https://help.madkudu.com/docs/customer-fit-how-to-create-or-edit-a-decision-tree"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="d-block text-decoration-none"
                                >
                                  <i
                                    aria-hidden
                                    className="fas fa-book-open me-1"
                                  />
                                  How to build or edit a tree
                                </a>
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <button
                            type="button"
                            className="tvz-help"
                            aria-label="How to read this tree"
                          >
                            <i aria-hidden className="fas fa-question-circle" />
                          </button>
                        </OverlayTrigger>
                      )}
                    </Card.Title>
                  </Col>
                  {/* Three ways to read the tree is a revamp feature. Off, the
                      page offers the one view it always offered. */}
                  {revamped && (
                    // `d-flex`, so the control is a flex item rather than an
                    // inline one. `.seg` is `inline-flex`, which puts it on a
                    // text baseline and makes the column reserve descender space
                    // beneath it; centring the column then centres the control
                    // PLUS that phantom space, and the control itself lands a
                    // couple of pixels high. As a flex item there is no line box
                    // and no descender, so the box centres on the title exactly.
                    <Col md="auto" className="d-flex">
                      <div
                        className="seg"
                        role="tablist"
                        aria-label="Tree view"
                      >
                        {TREE_VIEWS.map((view) => (
                          <button
                            key={view.key}
                            type="button"
                            role="tab"
                            aria-selected={this.state.view === view.key}
                            className={
                              this.state.view === view.key
                                ? 'seg-item seg-item--icon seg-item--on'
                                : 'seg-item seg-item--icon'
                            }
                            title={view.label}
                            aria-label={view.label}
                            onClick={() => this.setState({ view: view.key })}
                          >
                            <i className={view.icon} aria-hidden="true" />
                          </button>
                        ))}
                      </div>
                    </Col>
                  )}
                  {/* The key sits with the thing it explains.
                                    It had a card of its own down the left,
                                    which spent a whole card on one line of
                                    text and separated the colours from the
                                    nodes wearing them. */}
                  <Col className="tvz-key">
                    {revamped ? (
                      <SegmentLegend
                        thresholds={
                          this.props.store.ensemblingPage?.ensemble?.thresholds
                        }
                        fallback={
                          // A model with no thresholds keeps the
                          // gradient colouring, so it keeps the
                          // gradient key.
                          <img
                            src={TreeGradient}
                            alt="Tree gradient scale"
                            style={{ maxWidth: 190 }}
                          />
                        }
                      />
                    ) : (
                      // The key the page carried before the segment colours
                      // existed to explain.
                      <img
                        src={TreeGradient}
                        alt="Tree gradient scale"
                        style={{ maxWidth: 190 }}
                      />
                    )}
                  </Col>
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,
                    <Col className="float-end" xs="auto">
                      <Dropdown className="float-end">
                        <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={this.toggleHistory(treeId)}
                            disabled={!isAllowedToEdit}
                          >
                            History / undo
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={this.toggleAdvancedMode}
                            disabled={!isAllowedToEdit}
                          >
                            Advanced mode
                          </Dropdown.Item>

                          <Dropdown.Divider />
                          <Dropdown.Item
                            onClick={this.toggleResetTree}
                            disabled={resetingTree || !isAllowedToEdit}
                          >
                            {resetingTree ? 'Reseting...' : 'Reset to default'}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                  )}
                </Row>

                {/* The panel floats over the view, so all three
                                  views get it from one place and none of them
                                  has to know it exists. */}
                <div
                  className={`tvz-stage ${nodeId ? 'tvz-stage--panel' : ''}`}
                >
                  {revamped && this.state.view === 'table' ? (
                    <TreeNodeTable
                      precisions={tree.definition.precisions}
                      structure={tree.definition.structure}
                      thresholds={
                        this.props.store.ensemblingPage?.ensemble?.thresholds
                      }
                      selectedNodeId={nodeId}
                      onSelectNode={this.selectNode}
                      onHoverNode={this.setHoveredNodes}
                    />
                  ) : (
                    <TreeViz
                      treeId={treeId}
                      nodeId={nodeId}
                      store={this.props.store}
                      // A tenant without the revamp has no way to pick a view,
                      // but a stale 'table' in state must not reach a prop that
                      // only takes an orientation.
                      orientation={
                        this.state.view === 'table'
                          ? 'vertical'
                          : this.state.view
                      }
                      revamped={revamped}
                      selectNode={this.selectNode}
                      onHoverNode={this.setHoveredNodes}
                      setActualTransitionCoords={this.setActualTransitionCoords}
                      actualTransitionCoords={this.state.actualTransitionCoords}
                    />
                  )}
                  <Route
                    exact
                    path={`/tenant/${tenant}/models/${modelId}/model/trees/${treeId}/nodes/:nodeId`}
                    render={(props: any) => (
                      <NodeInspector
                        treeId={treeId}
                        nodeId={Number(props.match.params.nodeId)}
                        treeName={tree.name}
                        onClose={this.deselectNode}
                      />
                    )}
                  />
                  {/* Hovering shows the same figures without the
                                    actions, so a tree can be read by moving
                                    across it. Only while nothing is selected:
                                    otherwise the panel would keep swapping out
                                    from under the buttons being aimed at. */}
                  {/* Not in the table view. There the preview floats over the
                      very columns it repeats: the table already prints leads,
                      conversions, rate and lift for every node at once, so the
                      panel covers data in order to show the same data one row at
                      a time. In the two canvas views a node card holds almost
                      nothing, which is what the preview is for. */}
                  {revamped &&
                    this.state.view !== 'table' &&
                    !nodeId &&
                    this.state.hoveredNodes?.length === 1 && (
                      <NodeInspector
                        treeId={treeId}
                        nodeId={this.state.hoveredNodes[0]}
                        treeName={tree.name}
                        preview
                      />
                    )}
                </div>
              </Card.Body>
            </Card>
          </React.Suspense>
        ) : (
          <Spinner />
        );

        return (
          <>
            {confirmResetDialog}
            {advancedModeDialog}
            {historyDialog}
            {savingSplitConditionError && (
              <Error
                message={`Could not save split condition - ${savingSplitConditionError}`}
              />
            )}
            {deletingSplitConditionError && (
              <Error
                message={`Could not delete split condition - ${deletingSplitConditionError}`}
              />
            )}
            <>
              {
                // eslint-disable-next-line no-nested-ternary
                tree && tree.definition ? (
                  <Row className="tvz-grid">
                    {/* The tree and what it is worth, full width. The model
                        recommendations column that used to sit beside this is
                        gone: the chat rail answers the same question better and
                        on every tab, so a second reviewer on one page was two
                        surfaces competing to say the same thing.

                        Everything below is behind `studio-tree-insights`. Off,
                        this page is exactly what master renders. */}
                    {this.state.treeInsights && (
                      <Col lg={12}>
                        {/* Recall and precision for this tree, above the tree
                          itself, so the shape being edited below is read
                          against what it is currently worth. Leaves come off
                          the structure the page already holds: asking the store
                          for the tree again would blank `tree.definition` while
                          it refetched, unmounting everything inside this guard
                          including the component that asked. */}
                        <TreePerformanceCharts
                          treeId={treeId}
                          leaves={leavesOf(tree.definition.structure)}
                          thresholds={
                            this.props.store.ensemblingPage?.ensemble
                              ?.thresholds
                          }
                          open={this.state.performanceOpen}
                          onToggleOpen={() =>
                            this.setState((prev) => ({
                              performanceOpen: !prev.performanceOpen,
                            }))
                          }
                        />
                      </Col>
                    )}
                    {/* `=== false`, not `!`, so nothing is drawn while the flag
                        is still resolving: `null` would otherwise render these,
                        then take them away a moment later. */}
                    {/* The whole page as it was, arrangement included:
                        recommendations on the left, the tree in the middle, the
                        node panel on the right, the table and the curve below.
                        Restored as one structure because the arrangement is the
                        page: the pieces were recovered one at a time first, and
                        dropping them into the revamped layout put the
                        recommendations under the tree and the node panel across
                        the full width beneath it. */}
                    {this.state.treeInsights === false && (
                      <Col lg={12}>
                        {this.renderTreeHelpCard()}
                        {this.renderPreRevampTree({
                          tree,
                          treeId,
                          nodeId,
                          tenant,
                          modelId,
                          updatingTree,
                          resetingTree,
                          isAllowedToEdit,
                          ...this.preRevampPieces(tree, nodeId),
                        })}
                      </Col>
                    )}
                    {/* Its own full-width row. It used to move between here and
                        the performance column depending on whether the review
                        was open beside it; with the review gone there is only
                        one place for it. */}
                    {this.state.treeInsights === true && (
                      <Col lg={12}>{treeCard}</Col>
                    )}
                    {/* No gains curve with the flag ON. It was kept in both
                        states on the argument that the band charts read the same
                        tree a different way, so dropping it narrowed what the
                        page could answer. The decision goes the other way: the
                        revamped page states recall and precision in the bands
                        above, and a second curve saying the same thing in a
                        third notation is the clutter the revamp exists to
                        remove. Flag OFF it is untouched, inside
                        `renderPreRevampTree`, which is the only state that has
                        nothing else to answer the question. */}

                    {/* Insights for whichever node is selected, sitting under
                        the tree instead of a page away. Node 1 when nothing is
                        picked, which is what the removed model level Insights
                        tab used to show: an unconditioned node falls through to
                        the same whole-model figures on the server. */}
                    {this.state.treeInsights && (
                      <Col lg={12} className="tvz-insights">
                        {this.renderInsights(
                          treeId,
                          tree.definition.structure,
                          nodeId
                        )}
                      </Col>
                    )}
                  </Row>
                ) : // eslint-disable-next-line no-nested-ternary
                tree && tree.loading ? (
                  <Spinner />
                ) : tree && tree.error ? (
                  <Alert variant="danger">
                    <h4>
                      The tree could not be loaded for the following reason:
                    </h4>
                    <div dangerouslySetInnerHTML={{ __html: tree.error }} />
                    <br />
                    <ButtonGroup vertical>
                      <Button
                        onClick={this.toggleHistory(treeId)}
                        variant="info"
                      >
                        Revert tree to a previous state
                      </Button>
                    </ButtonGroup>
                  </Alert>
                ) : (
                  <Alert variant="info">No loaded tree.</Alert>
                )
              }
            </>
          </>
        );
      }
    }
  )
);

export default withRouter(TreePage);
